import { Button, Card, Label, Textarea } from "flowbite-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SitePageApi from "../../Api/SitePageApi";
import Loader from "../Common/Loader";
import { SitePage } from "../../Models/SitePage";

interface Props {
  /**
   * When provided the widget loads/saves that specific member's page (superuser use).
   * When omitted the widget loads/saves the logged-in user's own page.
   */
  memberId?: number;
  /** Called after a successful save, with the updated page. */
  onSaved?: (page: SitePage) => void;
}

export default function ShowcasePageWidget({ memberId, onSaved }: Props) {
  const [page, setPage] = useState<SitePage | null>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [showInNav, setShowInNav] = useState(false);
  const [validationResult, setValidationResult] = useState<{
    valid: boolean;
    message?: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    const request = memberId
      ? SitePageApi.getMemberPageById(memberId)
      : SitePageApi.getMyMemberPage();

    request
      .then((res) => {
        const data = res.data;
        setPage(data);
        setTitle(data.title ?? "");
        setDescription(data.description ?? "");
        setShowInNav(Boolean(data.showInNav));
        setForbidden(false);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          setForbidden(true);
          setPage(null);
        } else {
          toast.error("Failed to load your page. " + (err.response?.data?.message ?? err.message));
        }
      })
      .finally(() => setLoading(false));
  }, [memberId]);

  const handleValidate = () => {
    setValidationResult(null);
    SitePageApi.validateMemberHtml(description)
      .then((res) => {
        setValidationResult({
          valid: res.data.valid,
          message:
            res.data.errorMessage ??
            (res.data.valid
              ? "HTML is valid. You can save to show this page in the menu."
              : undefined),
        });
      })
      .catch(() => {
        setValidationResult({ valid: false, message: "Validation request failed." });
      });
  };

  const handleSave = () => {
    if (!page) return;
    setSaving(true);
    const payload: SitePage = {
      ...page,
      title: title || page.title,
      description: description || null,
      showInNav,
    };
    SitePageApi.addUpdateSitePage(payload)
      .then((res) => {
        if (res.data.success) {
          toast.success(res.data.message ?? "Page saved successfully.");
          const notInMenu = res.data.message?.includes("not appear in the menu");
          setValidationResult({
            valid: !notInMenu,
            message: notInMenu
              ? res.data.message
              : "Page saved and will appear in the menu.",
          });
          const updated: SitePage = {
            ...payload,
            id: Number(res.data.affectedEntityIdentifier),
            showInNav,
          };
          setPage(updated);
          onSaved?.(updated);
        } else {
          toast.error(res.data.message ?? "Save failed.");
        }
      })
      .catch((err) => {
        toast.error(err.response?.data?.message ?? "Failed to save.");
      })
      .finally(() => setSaving(false));
  };

  if (loading) return <Loader />;

  if (forbidden) {
    return (
      <p className="text-gray-600 text-sm">
        Only Platinum members can create a showcase page. If you believe you should have access,
        please contact an administrator.
      </p>
    );
  }

  return (
    <Card>
      <div className="space-y-4">
        <div>
          <Label htmlFor="sp-title">Page title (shown in menu)</Label>
          <input
            id="sp-title"
            type="text"
            className="mt-1 block w-full rounded border border-gray-300 p-2"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Our Services"
          />
        </div>
        <div>
          <Label htmlFor="sp-description">HTML content</Label>
          <Textarea
            id="sp-description"
            rows={12}
            className="mt-1 font-mono text-sm"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="<h2>Our Services</h2><p>Describe your company...</p>"
          />
          <p className="text-xs text-gray-500 mt-1">
            Do not use &lt;script&gt; tags, javascript: links, or inline event handlers (e.g.
            onclick=).
          </p>
        </div>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <input
              id="sp-showInNav"
              type="checkbox"
              checked={showInNav}
              onChange={(e) => setShowInNav(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="sp-showInNav">Make Page Active</Label>
          </div>
          <Button color="light" onClick={handleValidate}>
            Validate HTML
          </Button>
        </div>
        {validationResult && (
          <div
            className={`p-3 rounded ${
              validationResult.valid ? "bg-green-50 text-green-800" : "bg-amber-50 text-amber-800"
            }`}
          >
            {validationResult.valid ? "✓ " : "✗ "}
            {validationResult.message}
          </div>
        )}
        <div className="flex justify-end">
          <Button color="success" className="min-w-[96px]" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
